
/**
 * Swatch click -> swap the card's main image (no page reload).
 * Works in collection grid cards built from:
 *  - sections/custom-product-grid.liquid
 *  - snippets/product-image.liquid (uses .slider-product-img and .slider-img-main.card-media)
 *  - snippets/product-info.liquid (renders .card-swatches with button.swatch[data-image])
 */
(function () {
  const SELECTORS = {
    cardRoot: '.product-info-container',
    swatchBtn: '.card-swatches .swatch, .card-swatches .swatch-chip',
    // Main image inside product-image snippet:
    mainImage: '.slider-product-img .slider-img-main.card-media, .slider-product-img [data-card-image]'
  };

  // Given a node within a card, find the shared card article and the main image
  function getCardContext(fromNode) {
    if (!fromNode) return {};
    // Card structure: article.handbag-card > a > [product-image + product-info]
    // Swatches live inside .product-info-container; climb up to the <article>
    const article = fromNode.closest('article.handbag-card') || fromNode.closest(SELECTORS.cardRoot)?.closest('article.handbag-card');
    if (!article) return {};
    // The product-image snippet is a sibling within the same <a> link
    const mainImg = article.querySelector(SELECTORS.mainImage) || article.querySelector('.slider-product-img img');
    return { article, mainImg };
  }

  // Swap the visible <img> source; also normalize srcset if present
  function swapImage(imgEl, url) {
    if (!imgEl || !url) return;
    imgEl.src = url;
    if (typeof imgEl.srcset !== 'undefined') {
      imgEl.srcset = url + ' 1x';
    }
    if (imgEl.decode) imgEl.decode().catch(() => {});
  }

  // Set selected state for swatches within the same product-info container
  function setSelected(btn) {
    const info = btn.closest(SELECTORS.cardRoot);
    if (!info) return;
    info.querySelectorAll('.card-swatches .swatch').forEach(el => {
      el.classList.toggle('is-selected', el === btn || el.contains(btn));
      el.setAttribute('aria-pressed', el === btn ? 'true' : 'false');
    });
  }

  function getSwatchButton(target) {
    // Allow clicking on the inner .swatch-chip span
    const btn = target.closest('button.swatch');
    return btn;
  }

  function onActivate(target) {
    const btn = getSwatchButton(target);
    if (!btn) return;
    const url = btn.getAttribute('data-image');
    if (!url) return;
    const { mainImg } = getCardContext(btn);
    if (!mainImg) return;
    swapImage(mainImg, url);
    setSelected(btn);
  }

  function bind(root = document) {
    // Click support
    root.addEventListener('click', (e) => {
      // Accept clicks on button.swatch or its child .swatch-chip
      const isSwatchClick = e.target.closest('.card-swatches .swatch') || e.target.closest('.card-swatches .swatch-chip');
      if (!isSwatchClick) return;
      e.preventDefault();
      onActivate(e.target);
    });

    // Keyboard accessibility
    root.addEventListener('keydown', (e) => {
      const btn = e.target.closest('.card-swatches .swatch');
      if (!btn) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate(btn);
      }
    });

    // Initialize aria-pressed defaults
    root.querySelectorAll('.card-swatches .swatch').forEach(b => {
      if (!b.hasAttribute('aria-pressed')) b.setAttribute('aria-pressed', 'false');
    });
  }

  // Rebind for Section Rendering API and theme editor lifecycle
  function hookShopify() {
    document.addEventListener('shopify:section:load', () => bind(document));
    document.addEventListener('shopify:section:select', () => bind(document));
    document.addEventListener('shopify:block:select', () => bind(document));
  }

  // Observe dynamic inserts (AJAX pagination, filters)
  function observeInserts() {
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('.handbag-card, .product-info-container, .card-swatches') ||
              node.querySelector?.('.card-swatches')) {
            // Ensure accessibility state on newly added swatches
            node.querySelectorAll?.('.card-swatches .swatch').forEach(b => {
              if (!b.hasAttribute('aria-pressed')) b.setAttribute('aria-pressed', 'false');
            });
          }
        }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bind(document);
      hookShopify();
      observeInserts();
    }, { once: true });
  } else {
    bind(document);
    hookShopify();
    observeInserts();
  }
})();
